// src/components/settings/ReadingsConfigPanel.tsx
import React from 'react';
import './InDesignExportSettings.css';

// ============================================================================
// Type Definitions for Lectionary Data
// ============================================================================

/** A single reading (OT, Psalm, NT, or Gospel) */
export interface LectionaryReading {
  reference: string;
  intro?: string;
  text?: string;
  label?: string; // For alternate readings like "Magnificat"
}

/** A reading slot that may have multiple options */
export interface LectionaryReadingWithOptions {
  options: LectionaryReading[];
  default: number;
}

/** Standard readings object (non-track entry) */
export interface LectionaryReadings {
  ot: LectionaryReading | LectionaryReadingWithOptions;
  psalm: LectionaryReading | LectionaryReadingWithOptions;
  nt: LectionaryReading | LectionaryReadingWithOptions;
  gospel: LectionaryReading | LectionaryReadingWithOptions;
}

/** Track-based readings (for Pentecost Propers) — numbered to match lectionarypage.net */
export interface LectionaryTracks {
  '1': LectionaryReadings; // Semi-continuous (continuous OT narrative)
  '2': LectionaryReadings; // Complementary (OT thematically paired with Gospel)
}

/** A lectionary entry - can be standard or track-based */
export interface LectionaryEntry {
  season: string;
  displayName: {
    en: string;
    es: string;
  };
  collect?: string;
  hasTracks?: boolean;
  tracks?: LectionaryTracks;
  // Standard readings (when hasTracks is false/undefined)
  palms?: LectionaryReading | LectionaryReadingWithOptions;
  ot?: LectionaryReading | LectionaryReadingWithOptions;
  psalm?: LectionaryReading | LectionaryReadingWithOptions;
  nt?: LectionaryReading | LectionaryReadingWithOptions;
  gospel?: LectionaryReading | LectionaryReadingWithOptions;
  /**
   * Alternative versions of the whole set of readings (Palm Sunday: St Luke's
   * "modern" default vs the prayer book's "traditional"). The top-level slots
   * are the default version; each named version overrides only what it lists.
   */
  versions?: Record<string, Partial<LectionaryReadings> & { palms?: LectionaryReading | LectionaryReadingWithOptions }>;
  defaultVersion?: string;
  versionLabels?: Record<string, string>;
  /** Easter Vigil: all nine lessons, and St Luke's usual picks (indices) */
  vigilLessons?: Array<{ title: string; reading: LectionaryReading | LectionaryReadingWithOptions; response?: LectionaryReading | LectionaryReadingWithOptions }>;
  defaultVigilLessons?: number[];
}

/** User's reading selections */
export interface ReadingSelections {
  track?: '1' | '2';
  /** '' or undefined = the day's default version */
  version?: string;
  /** Optional verses in [brackets]: kept unless 'drop' */
  optionalVerses?: '' | 'keep' | 'drop';
  /** Easter Vigil lessons to include (indices); undefined = the day's default */
  vigilLessons?: number[];
  /** Easter Vigil: print each lesson's appointed psalm/canticle */
  vigilResponses?: boolean;
  options: {
    ot?: number;
    psalm?: number;
    nt?: number;
    gospel?: number;
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/** Check if a reading has options */
function hasOptions(reading: LectionaryReading | LectionaryReadingWithOptions | undefined): reading is LectionaryReadingWithOptions {
  return reading !== undefined && 'options' in reading && Array.isArray(reading.options);
}

/** Get the selected reading from a slot */
function getSelectedReading(
  reading: LectionaryReading | LectionaryReadingWithOptions | undefined,
  selectedIndex?: number
): LectionaryReading | undefined {
  if (!reading) return undefined;
  if (hasOptions(reading)) {
    const index = selectedIndex ?? reading.default;
    return reading.options[index];
  }
  return reading;
}

type ActiveReadings = LectionaryReadings & { palms?: LectionaryReading | LectionaryReadingWithOptions };

/** Get readings from an entry (handling tracks and versions) */
function getReadingsFromEntry(entry: LectionaryEntry, selectedTrack?: '1' | '2', version?: string): ActiveReadings | null {
  if (entry.hasTracks && entry.tracks) {
    const track = selectedTrack || '1';
    return entry.tracks[track];
  }
  // Standard entry - build readings object
  if (entry.ot && entry.psalm && entry.nt && entry.gospel) {
    const base: ActiveReadings = {
      palms: entry.palms,
      ot: entry.ot,
      psalm: entry.psalm,
      nt: entry.nt,
      gospel: entry.gospel,
    };
    const override = version && entry.versions ? entry.versions[version] : undefined;
    return override ? { ...base, ...override } as ActiveReadings : base;
  }
  return null;
}

interface VersionSelectorProps {
  entry: LectionaryEntry;
  selected: string;
  onChange: (version: string) => void;
}

/** Choose between a day's versions (Palm Sunday); the day's default is listed first. */
const VersionSelector: React.FC<VersionSelectorProps> = ({ entry, selected, onChange }) => {
  const def = entry.defaultVersion || 'modern';
  const names = [def, ...Object.keys(entry.versions || {}).filter(v => v !== def)];
  const label = (v: string) => entry.versionLabels?.[v] || v;
  return (
    <div className="reading-slot">
      <div className="reading-slot-header">
        <span className="reading-slot-label">Version:</span>
        <select className="reading-option-select" value={selected || def}
          onChange={(e) => onChange(e.target.value === def ? '' : e.target.value)}>
          {names.map(v => (
            <option key={v} value={v}>{label(v)}{v === def ? ' (default)' : ''}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

// ============================================================================
// Sub-Components
// ============================================================================

interface VigilPickerProps {
  entry: LectionaryEntry;
  selections: ReadingSelections;
  onChange: (next: ReadingSelections) => void;
}

/** Easter Vigil: tick the lessons that go in the bulletin (default: St Luke's three). */
const VigilPicker: React.FC<VigilPickerProps> = ({ entry, selections, onChange }) => {
  const lessons = entry.vigilLessons || [];
  const picked = selections.vigilLessons ?? entry.defaultVigilLessons ?? lessons.map((_, i) => i);
  const toggle = (i: number) => {
    const next = picked.includes(i) ? picked.filter(x => x !== i) : [...picked, i].sort((a, b) => a - b);
    onChange({ ...selections, vigilLessons: next });
  };
  return (
    <div className="reading-slot">
      <div className="reading-slot-header">
        <span className="reading-slot-label">Vigil lessons:</span>
      </div>
      {lessons.map((l, i) => {
        const ref = getSelectedReading(l.reading)?.reference || '';
        return (
          <label key={i} className="vigil-lesson-option" style={{ display: 'block' }}>
            <input type="checkbox" checked={picked.includes(i)} onChange={() => toggle(i)} />{' '}
            {l.title} <span className="reading-slot-reference">({ref})</span>
          </label>
        );
      })}
      <label style={{ display: 'block', marginTop: '0.4em' }}>
        <input type="checkbox" checked={!!selections.vigilResponses}
          onChange={(e) => onChange({ ...selections, vigilResponses: e.target.checked })} />{' '}
        Print each lesson&apos;s appointed psalm or canticle (off: hymns placed by hand)
      </label>
      {picked.length === 0 && <div className="readings-config-error">Pick at least one lesson.</div>}
    </div>
  );
};

interface TrackSelectorProps {
  selectedTrack: '1' | '2';
  onChange: (track: '1' | '2') => void;
}

const TrackSelector: React.FC<TrackSelectorProps> = ({ selectedTrack, onChange }) => {
  return (
    <div className="track-selector">
      <span className="track-selector-label">Select Track:</span>
      <div className="track-toggle">
        <button
          type="button"
          className={`track-button ${selectedTrack === '1' ? 'selected' : ''}`}
          onClick={() => onChange('1')}
        >
          <span className="track-letter">Track 1</span>
          <span className="track-description">Semi-continuous</span>
        </button>
        <button
          type="button"
          className={`track-button ${selectedTrack === '2' ? 'selected' : ''}`}
          onClick={() => onChange('2')}
        >
          <span className="track-letter">Track 2</span>
          <span className="track-description">Complementary</span>
        </button>
      </div>
    </div>
  );
};

interface ReadingSlotProps {
  slotType: 'palms' | 'ot' | 'psalm' | 'nt' | 'gospel';
  reading: LectionaryReading | LectionaryReadingWithOptions | undefined;
  selectedOption?: number;
  onOptionChange?: (index: number) => void;
}

const SLOT_LABELS: Record<string, string> = {
  palms: 'Liturgy of the Palms',
  ot: 'First Reading',
  psalm: 'Psalm',
  nt: 'Second Reading',
  gospel: 'Gospel',
};

const ReadingSlot: React.FC<ReadingSlotProps> = ({
  slotType,
  reading,
  selectedOption,
  onOptionChange,
}) => {
  if (!reading) return null;

  const hasMultipleOptions = hasOptions(reading);
  const selectedReading = getSelectedReading(reading, selectedOption);

  return (
    <div className="reading-slot">
      <div className="reading-slot-header">
        <span className="reading-slot-label">{SLOT_LABELS[slotType]}:</span>
        {hasMultipleOptions && (
          <select
            className="reading-option-select"
            value={selectedOption ?? reading.default}
            onChange={(e) => onOptionChange?.(parseInt(e.target.value, 10))}
          >
            {reading.options.map((opt, idx) => (
              <option key={idx} value={idx}>
                {opt.label || opt.reference}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="reading-slot-reference">
        {selectedReading?.reference || 'N/A'}
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

interface ReadingsConfigPanelProps {
  /** The lectionary entry for the selected service */
  entry: LectionaryEntry | null;
  /** Current reading selections */
  selections: ReadingSelections;
  /** Callback when selections change */
  onSelectionsChange: (selections: ReadingSelections) => void;
  /** Whether data is loading */
  loading?: boolean;
  /** Error message if lectionary data couldn't be loaded */
  error?: string | null;
}

export const ReadingsConfigPanel: React.FC<ReadingsConfigPanelProps> = ({
  entry,
  selections,
  onSelectionsChange,
  loading = false,
  error = null,
}) => {
  // Handle track change
  const handleTrackChange = (track: '1' | '2') => {
    onSelectionsChange({
      ...selections,
      track,
      // Reset option selections when changing tracks
      options: {},
    });
  };

  // Handle version change (resets option picks: the slots change with the version)
  const handleVersionChange = (version: string) => {
    onSelectionsChange({ ...selections, version, options: {} });
  };

  // Handle option change for a specific slot
  const handleOptionChange = (slot: 'ot' | 'psalm' | 'nt' | 'gospel', index: number) => {
    onSelectionsChange({
      ...selections,
      options: {
        ...selections.options,
        [slot]: index,
      },
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="readings-config-panel">
        <div className="readings-config-loading">Loading lectionary data...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="readings-config-panel">
        <div className="readings-config-error">{error}</div>
      </div>
    );
  }

  // No entry selected
  if (!entry) {
    return null;
  }

  // Get the active readings based on track selection
  const activeReadings = getReadingsFromEntry(entry, selections.track, selections.version || entry.defaultVersion);

  if (!activeReadings) {
    return (
      <div className="readings-config-panel">
        <div className="readings-config-error">No readings available for this entry</div>
      </div>
    );
  }

  // Check if any slot has options
  const hasAnyOptions =
    hasOptions(activeReadings.ot) ||
    hasOptions(activeReadings.psalm) ||
    hasOptions(activeReadings.nt) ||
    hasOptions(activeReadings.gospel);

  return (
    <div className="readings-config-panel">
      <h4>Lectionary Readings</h4>

      {/* Track selector for Pentecost Propers */}
      {entry.hasTracks && (
        <TrackSelector
          selectedTrack={selections.track || '1'}
          onChange={handleTrackChange}
        />
      )}

      {/* Version selector (Palm Sunday) */}
      {entry.versions && (
        <VersionSelector entry={entry} selected={selections.version || ''} onChange={handleVersionChange} />
      )}

      {/* Reading slots */}
      <div className="readings-slots">
        {activeReadings.palms && (
          <ReadingSlot slotType="palms" reading={activeReadings.palms} />
        )}
        <ReadingSlot
          slotType="ot"
          reading={activeReadings.ot}
          selectedOption={selections.options.ot}
          onOptionChange={(idx) => handleOptionChange('ot', idx)}
        />
        <ReadingSlot
          slotType="psalm"
          reading={activeReadings.psalm}
          selectedOption={selections.options.psalm}
          onOptionChange={(idx) => handleOptionChange('psalm', idx)}
        />
        <ReadingSlot
          slotType="nt"
          reading={activeReadings.nt}
          selectedOption={selections.options.nt}
          onOptionChange={(idx) => handleOptionChange('nt', idx)}
        />
        <ReadingSlot
          slotType="gospel"
          reading={activeReadings.gospel}
          selectedOption={selections.options.gospel}
          onOptionChange={(idx) => handleOptionChange('gospel', idx)}
        />
      </div>

      {/* Easter Vigil lessons */}
      {entry.vigilLessons && (
        <VigilPicker entry={entry} selections={selections} onChange={onSelectionsChange} />
      )}

      {/* Optional verses (in [brackets] in the text; the reference drops them too) */}
      <label className="optional-verses-option" style={{ display: 'block', marginTop: '0.5em' }}>
        <input type="checkbox" checked={selections.optionalVerses !== 'drop'}
          onChange={(e) => onSelectionsChange({ ...selections, optionalVerses: e.target.checked ? '' : 'drop' })} />{' '}
        Include optional verses [in brackets]
      </label>

      {/* Info text */}
      {!hasAnyOptions && !entry.hasTracks && !entry.versions && (
        <p className="readings-info-text">
          Standard readings for this service. No selections needed.
        </p>
      )}
    </div>
  );
};

export default ReadingsConfigPanel;
